import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const PLAIN_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const getAppLocale = () => {
  const currentLang = (typeof window !== 'undefined' && localStorage.getItem('app-language')) || 'da';
  return currentLang === 'en' ? 'en-US' : 'da-DK';
};

// Extract YYYY-MM-DD components from either a plain string or a timestamp.
// Timestamps are read using UTC components, because date-only values were
// originally stored as UTC midnight (`new Date("YYYY-MM-DD")`). Using local
// components would shift the day for viewers west of UTC.
const extractYmd = (value: string | Date): { year: number; month: number; day: number } | null => {
  if (typeof value === 'string') {
    if (PLAIN_DATE_REGEX.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return { year, month, day };
    }
    if (value.includes('T')) {
      const datePart = value.split('T')[0];
      if (PLAIN_DATE_REGEX.test(datePart)) {
        const [year, month, day] = datePart.split('-').map(Number);
        return { year, month, day };
      }
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return {
      year: parsed.getUTCFullYear(),
      month: parsed.getUTCMonth() + 1,
      day: parsed.getUTCDate(),
    };
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
    };
  }
  return null;
};

// Format a plain calendar date (no timezone) using the current app locale.
// Accepts either a YYYY-MM-DD string or a Date/ISO timestamp; in either case
// the day shown is the day the user originally typed.
export const formatPlainDate = (value: string | Date | null | undefined) => {
  if (!value) return "";
  const ymd = extractYmd(value);
  if (!ymd) return "";
  // Build a UTC date so the locale formatter renders the exact day we passed in.
  const utcDate = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day, 12, 0, 0));
  return new Intl.DateTimeFormat(getAppLocale(), {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(utcDate);
};

// formatDate stays backward compatible but now uses a timezone-safe path so
// pure calendar dates (birthdate, occasion date, rsvp deadline) never drift.
export const formatDate = (date: string | Date) => {
  if (!date) return "";
  return formatPlainDate(date);
};

export const calculateAge = (birthdate: string | Date) => {
  if (!birthdate) return "";
  const ymd = extractYmd(birthdate);
  if (!ymd) return "";

  const today = new Date();
  let age = today.getFullYear() - ymd.year;
  const monthDiff = (today.getMonth() + 1) - ymd.month;

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < ymd.day)) {
    age--;
  }

  return age;
};

export const getRandomColor = () => {
  const colors = ["soft-pink", "mint", "light-blue", "cream"];
  return colors[Math.floor(Math.random() * colors.length)];
};

// === TIMEZONE UTILITIES (Native APIs only - no bundle size impact) ===

// Get user's current timezone
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Convert local datetime-local input to UTC for database storage
export const localToUTC = (localDateTimeString: string): Date => {
  // datetime-local format: "2025-09-08T17:00"
  // This represents user's local time, we need to convert to UTC
  return new Date(localDateTimeString);
};

// Convert UTC datetime to user's local time for display
export const utcToLocal = (utcDate: string | Date): Date => {
  return new Date(utcDate);
};

// Format datetime with timezone context for user
export const formatDateTimeWithTimezone = (date: string | Date) => {
  if (!date) return "";
  const d = new Date(date);
  const currentLang = localStorage.getItem('app-language') || 'da';
  const locale = currentLang === 'en' ? 'en-US' : 'da-DK';
  
  return d.toLocaleString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short"
  });
};

// Get relative time description (e.g., "in 2 hours", "3 minutes ago")
export const getRelativeTime = (date: string | Date) => {
  if (!date) return "";
  const now = new Date();
  const target = new Date(date);
  const diffMs = target.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60000);
  
  if (Math.abs(diffMins) < 1) return "Now";
  if (diffMins > 0) {
    if (diffMins < 60) return `In ${diffMins} min`;
    if (diffMins < 1440) return `In ${Math.round(diffMins / 60)} hours`;
    return `In ${Math.round(diffMins / 1440)} days`;
  } else {
    const absMins = Math.abs(diffMins);
    if (absMins < 60) return `${absMins} min ago`;
    if (absMins < 1440) return `${Math.round(absMins / 60)} hours ago`;
    return `${Math.round(absMins / 1440)} days ago`;
  }
};

// === GIFT TIMING LOGIC (Timezone-Aware) ===

// Check if gift should be visible based on timing (timezone-aware)
export const isGiftVisible = (gift: { hasTimingRestriction?: boolean | null; giftArrivalDate?: string | Date | null }) => {
  // If no timing restriction is set, gift is visible immediately
  if (!gift.hasTimingRestriction || !gift.giftArrivalDate) {
    return true;
  }
  
  // Compare current time with arrival time (both in user's timezone)
  const currentTime = new Date();
  const arrivalTime = new Date(gift.giftArrivalDate);
  
  // Gift is visible if current time >= arrival time
  return currentTime >= arrivalTime;
};

// Check if gift is newly arrived and not yet viewed (for gift table)
export const isGiftNewlyArrived = (gift: { 
  hasTimingRestriction?: boolean | null; 
  giftArrivalDate?: string | Date | null;
  lastViewedAt?: string | Date | null;
}) => {
  // Gift must be visible first
  if (!isGiftVisible(gift)) {
    return false;
  }
  
  // If never viewed, it's newly arrived
  if (!gift.lastViewedAt) {
    return true;
  }
  
  // If no timing restriction, it's not "newly" arrived (was always available)
  if (!gift.hasTimingRestriction || !gift.giftArrivalDate) {
    return false;
  }
  
  // Check if it was viewed before its arrival time (impossible, so it's newly arrived)
  const arrivalTime = new Date(gift.giftArrivalDate);
  const lastViewedTime = new Date(gift.lastViewedAt);
  
  return lastViewedTime < arrivalTime;
};

// Count how many gifts are scheduled to arrive later (for notification badge)
export const getScheduledGiftsCount = (gifts: Array<{ hasTimingRestriction?: boolean | null; giftArrivalDate?: string | Date | null }>) => {
  return gifts.filter(gift => 
    gift.hasTimingRestriction && 
    gift.giftArrivalDate && 
    !isGiftVisible(gift)
  ).length;
};
